import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import DahsboardReportService from 'services/dashboard';
import { useNavigate } from 'react-router-dom';
import { message, theme } from 'antd';
import { useWorkSpace } from 'contexts/workspace';
import {
  AppstoreOutlined,
  BarChartOutlined,
  CarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  GiftOutlined,
  PrinterOutlined,
  ShoppingOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';

const service = new DahsboardReportService();
const WebsiteStatisticContext = createContext();

const quickActions = [
  {
    key: 'orders',
    label: 'Đơn hàng',
    path: '/orders',
    icon: <FileTextOutlined />,
    tone: 'primary',
  },
  {
    key: 'customers',
    label: 'Khách hàng',
    path: '/customers',
    icon: <TeamOutlined />,
    tone: 'warning',
  },
  {
    key: 'products',
    label: 'Sản phẩm',
    path: '/products',
    icon: <ShoppingOutlined />,
    tone: 'success',
  },
  {
    key: 'voucher',
    label: 'Khuyến mãi',
    path: '/vouchers',
    icon: <GiftOutlined />,
    tone: 'primary',
  },
  {
    key: 'namecard',
    label: 'Danh thiếp',
    path: '/account/shoplist',
    icon: <UserOutlined />,
    tone: 'warning',
  },
  {
    key: 'print',
    label: 'Máy in',
    path: '/website/setting/print',
    icon: <PrinterOutlined />,
    tone: 'primary',
  },
  {
    key: 'report',
    label: 'Báo cáo',
    path: '/report/shop',
    icon: <BarChartOutlined />,
    tone: 'warning',
  },
  {
    key: 'all',
    label: 'Tất cả',
    path: '__open_mobile_menu__',
    icon: <AppstoreOutlined />,
    tone: 'success',
  },
];

const useDashboard = (isMobile = false) => {
  const todayRange = {
    start: dayjs().startOf('d').toDate(),
    end: dayjs().endOf('d').toDate(),
  };

  const [data, setData] = useState({});
  const { workspace } = useWorkSpace();
  const websiteUrl = String(workspace?.websiteUrl || '').replace(/\/$/, '');

  const [filter, setFilter] = useState({
    range: isMobile
      ? todayRange
      : {
        start: dayjs().add(-7, 'd').toDate(),
        end: dayjs().toDate(),
      },
  });
  const navigate = useNavigate();
  const [messageApi, messageContextHolder] = message.useMessage();
  const { token } = theme.useToken();

  useEffect(() => {
    if (!isMobile) {
      loadDataByFilter();
    }
  }, [filter, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    const mobileTodayRange = {
      start: dayjs().startOf('d').toDate(),
      end: dayjs().endOf('d').toDate(),
    };

    setFilter((prev) => ({
      ...prev,
      range: mobileTodayRange,
    }));
    loadDataSummary(mobileTodayRange);
  }, [isMobile]);

  const loadDataByFilter = async () => {
    let range = null;

    if (filter.range) {
      range = {
        start: filter.range.start,
        end: filter.range.end,
      };
    }

    const dt = await service.dashboard({ range });
    setData(dt);
  };

  const loadDataSummary = async (range = null) => {
    const dt = await service.dashboard({ range });
    setData(dt);
  };

  const setFilterValue = (name, value) => {
    const newFilter = { ...filter };
    newFilter[name] = value;
    setFilter(newFilter);
  };

  const onDateRangeFilterChange = (e) => {
    if (!e) {
      setFilterValue('range', null);
    } else {
      setFilterValue('range', {
        start: e[0].startOf('d').format(),
        end: e[1].endOf('d').format(),
      });
    }

    console.log('e', e);
  };

  const rangePickerPresets = useMemo(() => ([
    { label: 'Hôm nay', value: [dayjs().add(0, 'd'), dayjs().add(0, 'd')] },
    { label: 'Hôm qua', value: [dayjs().add(-1, 'd'), dayjs().add(-1, 'd')] },
    { label: '7 ngày qua', value: [dayjs().add(-7, 'd'), dayjs().add(0, 'd')] },
    { label: '30 ngày qua', value: [dayjs().add(30, 'd'), dayjs().add(0, 'd')] },
  ]), []);

  const rangePickerValue = filter.range
    ? [dayjs(filter.range.start), dayjs(filter.range.end)]
    : null;

  const orderNewCount = data?.order_new_count ?? data?.new_order_count ?? data?.order_count ?? 0;
  const orderShippingCount = data?.order_shipping_count ?? data?.shipping_order_count ?? 0;
  const orderCancelCount = data?.order_cancel_count ?? data?.cancel_order_count ?? 0;
  const orderAmount = data?.order_amount ?? 0;
  const storeLink = websiteUrl || '';
  const storeDomain = storeLink ? storeLink.replace(/^https?:\/\//, '') : '';

  const getActionColor = (tone) => {
    if (tone === 'warning') {
      return token.colorWarning;
    }
    if (tone === 'success') {
      return token.colorSuccess;
    }

    return token.colorPrimary;
  };

  const onQuickActionClick = (path) => {
    if (path === '__open_mobile_menu__') {
      window.dispatchEvent(new CustomEvent('open-mobile-app-menu'));
      return;
    }

    navigate(path);
  };

  const onViewReport = () => {
    navigate('/website/report');
  };

  const onCreateOrder = () => {
    navigate('/order/add');
  };

  const onCopyStoreLink = async () => {
    if (!storeLink) {
      messageApi.warning('Chưa có liên kết cửa hàng');
      return;
    }

    try {
      await navigator.clipboard.writeText(storeLink);
      messageApi.success('Đã sao chép liên kết cửa hàng');
    } catch {
      messageApi.error('Không thể sao chép liên kết');
    }
  };

  const onShareStoreLink = async () => {
    if (!storeLink) {
      messageApi.warning('Chưa có liên kết cửa hàng');
      return;
    }

    try {
      if (navigator.share) {
        await navigator.share({ title: 'Cửa hàng online', url: storeLink });
        return;
      }

      await navigator.clipboard.writeText(storeLink);
      messageApi.success('Thiết bị không hỗ trợ chia sẻ, đã sao chép liên kết');
    } catch {
      messageApi.error('Không thể chia sẻ liên kết');
    }
  };

  return {
    data,
    websiteUrl,
    filter,
    setFilter,
    onDateRangeFilterChange,
    rangePickerPresets,
    rangePickerValue,
    messageContextHolder,
    quickActions,
    getActionColor,
    orderNewCount,
    orderShippingCount,
    orderCancelCount,
    orderAmount,
    storeLink,
    storeDomain,
    onQuickActionClick,
    onViewReport,
    onCreateOrder,
    onCopyStoreLink,
    onShareStoreLink,
  };
};

const WebsiteStatisticProvider = ({ value, children }) => {
  return (
    <WebsiteStatisticContext.Provider
      value={{ data: value.data, filter: value.filter, setFilter: value.setFilter, websiteUrl: value.websiteUrl }}
    >
      {children}
    </WebsiteStatisticContext.Provider>
  );
};

const useWebsiteStattistic = () => {
  return useContext(WebsiteStatisticContext);
};

export { WebsiteStatisticProvider, useWebsiteStattistic };
export default useDashboard;
